import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useListVouchers, useCreateVoucher } from '../services/appService';

function VouchersPage() {
  const { appId } = useParams();
  const listVouchers = useListVouchers();
  const createVoucher = useCreateVoucher();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [creating, setCreating] = useState(false);
  
  const fetchVouchers = async () => {
    if (!appId) return;
    setLoading(true);
    try {
      const data = await listVouchers(appId);
      setVouchers(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load vouchers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVouchers(); }, [appId]);

  const handleCreate = async () => {
    if (!code) { setError('Code is required'); return; }
    setCreating(true);
    setError('');
    try {
      await createVoucher(appId, { code, expires_at: expiresAt || null });
      setCode('');
      setExpiresAt('');
      fetchVouchers();
    } catch (err) {
      setError(err.message || 'Failed to create voucher.');
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 240 },
    { field: 'code', headerName: 'Code', width: 200 },
    { field: 'is_used', headerName: 'Used', width: 100, type: 'boolean' },
    { field: 'created_at', headerName: 'Created At', width: 180 },
    { field: 'used_at', headerName: 'Used At', width: 180 },
    { field: 'expires_at', headerName: 'Expires At', width: 180 },
    { field: 'user_id', headerName: 'Redeemed By', width: 240 }
  ];

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Typography variant="h4" gutterBottom>Manage Vouchers</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Voucher Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={creating}
        />
        <TextField
          label="Expires At"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          disabled={creating}
        />
        <Button
          variant="contained"
          startIcon={<LocalOfferIcon />}
          onClick={handleCreate}
          disabled={creating}
        >
          {creating ? <CircularProgress size={24} /> : 'Create'}
        </Button>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={vouchers}
          columns={columns}
          autoHeight
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          components={{ Toolbar: GridToolbar }}
          getRowId={(row) => row.id}
        />
      )}
    </Box>
  );
}

export default VouchersPage; 